// Render-facing derived keys — close the §IV stitch gaps (round 2, s322).
//
// detect() already emits the carrier curve + members, but the mapping-spec Source
// column references three things that had no concrete key:
//   • combinations.carrier_config — the §2.9 rendering-matrix row (6 values)
//   • the pale-line draw set (s310.d2) — distinct from redLine.paleLines (complement)
//   • "where it stands now" — a per-current-stage snapshot (§III footer + §IV card)
// All three are deterministic derivations (no LLM, no new detection). The engine
// is otherwise unchanged.

import { isTouchedAt } from './input.js';

/**
 * Carrier-config — the single §2.9 row key the spec branches on.
 * NB: 'triplet' fires when a 'together' main has exactly 3 co-parallel members
 * (the §3.3.2 set-assembly rule / Rule 5, in the engine since s315 —
 * detectors.js `triplet-as-one`, ≥3 mutually-parallel). Verified live (s5 tail).
 * It is rare on the all-touched fixtures (bench-29 / taxonomy-36): a co-parallel
 * trio inside a fully-touched field either extends to 4+ → 'field' or is
 * outranked → 'singleton'; a live triplet needs input with ~3 touched layers.
 */
export function carrierConfigOf(main, carrier) {
  const n = carrier.members.length;
  switch (main.family) {
    case 'singleton':
      return 'singleton';
    case 'scatter':
      return 'scatter';
    case 'field': // field-level-recovery — a "field" carrier-config (dip geometry)
      return 'field';
    case 'opposition':
      return 'split';
    case 'together': // flat 4+ co-parallel is also "field" carrier-config (s315.d2)
      return n >= 4 ? 'field' : n === 3 ? 'triplet' : 'pair';
    default:
      return 'scatter';
  }
}

/**
 * Pale-line draw set per s310.d2 (what §IV.chart draws pale) — NOT redLine.paleLines
 * (which is the complement of active layers). Confirmed direction: s310.d2 is right;
 * this is the render draw set. Render still applies the ≥7-member omit ("all seven"
 * = §III dup) on top.
 *   singleton → []           (one raw line, nothing folded)
 *   pair/triplet/field → members (the folded lines; 2 / 3 / 4+)
 *   split-pair → [opposing]   (one opposing line, s310.d2 "одна противоположная")
 *   group-vs-group → members  (dominant block, "по числу членов")
 *   scatter → []              (silhouette, no pale lines)
 */
export function carrierSourceLines(main, carrier) {
  switch (main.family) {
    case 'singleton':
    case 'scatter':
      return [];
    case 'together':
    case 'field':
      return carrier.members.slice();
    case 'opposition': {
      if (main.type === 'group-vs-group') return carrier.members.slice();
      const opposing = main.layers.find((L) => !carrier.members.includes(L));
      return opposing ? [opposing] : [];
    }
    default:
      return [];
  }
}

const median = (arr) => {
  const s = [...arr].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/**
 * "Where it stands now" — snapshot of the CURRENT stage over the touched 7 layers
 * (mapping-spec §III.footer_where_you_are_now / §IV.claim_cards.where_it_stands_now,
 * shared engine, different copy). Reads current-stage values only; NOT the carrier,
 * NOT taxonomy. Emits the structural facts; render chooses the phrase from the fields.
 *
 * Algorithm (s390.d4 — 7 states):
 *   • cloud = largest set within a band width ≤1, real only if size ≥5.
 *   • level by median: ≤−1 low, ≥+1 high, otherwise (around zero) mid — a band that
 *     straddles zero is "in the middle", not a rounded-up high.
 *   • candidate outlier = value with a gap ≥2 from the nearest cloud edge.
 *   • FOLD RULE: a candidate on the SAME side the cloud sits (a floor cloud with a
 *     layer still lower, a high cloud with one still higher) is not standing apart —
 *     it folds into the cloud (size grows, band widens toward its level) and is NOT an
 *     outlier. A mid cloud has no home side, so nothing folds. This changes the
 *     structural fact, hence it lives in the engine, not render.
 * The states render then selects from the fields:
 *   cloud null                       → scatter ("nothing holds together")
 *   cloud + 0 outliers               → all-together at cloud.level (low / mid / high)
 *   cloud + 1 outlier                → cloud.level × outlier.dir
 *   cloud + 2+ outliers (one side)   → "{N} on {dir} while {cloud.size} at {level}"
 */
export function currentStanding(stages, active) {
  const N = stages.length;
  // "Where it stands NOW" reads only layers ANSWERED at the current stage (s443.d5 Ф2):
  // an untouched-at-NOW slider carries a default 0, not a reading — including it would
  // fabricate a floor member / inflate the cloud. On all-touched this filter is a no-op.
  const pts = active
    .filter((L) => isTouchedAt(stages[N - 1], L))
    .map((L) => ({ L, v: stages[N - 1].values[L] }))
    .sort((a, b) => a.v - b.v);
  const T = pts.length;

  if (T === 0) {
    // Current stage fully untouched — no "now" to read.
    return { touchedCount: 0, cloud: null, outlier: null, outliers: [] };
  }

  let best = { i: 0, j: 0 };
  for (let i = 0; i < T; i++) {
    let j = i;
    while (j + 1 < T && pts[j + 1].v - pts[i].v <= 1) j++;
    if (j - i > best.j - best.i) best = { i, j };
  }
  const cloudPts = pts.slice(best.i, best.j + 1);
  const hasCloud = cloudPts.length >= 5;

  if (!hasCloud) {
    return { touchedCount: T, cloud: null, outlier: null, outliers: [] };
  }

  let cloudMin = Math.min(...cloudPts.map((p) => p.v));
  let cloudMax = Math.max(...cloudPts.map((p) => p.v));
  const med = median(cloudPts.map((p) => p.v));
  const level = med <= -1 ? 'low' : med >= 1 ? 'high' : 'mid';

  const candidates = pts
    .filter((p) => p.v - cloudMax >= 2 || cloudMin - p.v >= 2)
    .map((p) => ({ layer: p.L, value: p.v, dir: p.v > cloudMax ? 'above' : 'below' }));

  // Fold home-side candidates back into the cloud; keep only the ones standing apart.
  const homeSide = level === 'low' ? 'below' : level === 'high' ? 'above' : null;
  const outliers = homeSide ? candidates.filter((o) => o.dir !== homeSide) : candidates;
  let size = cloudPts.length;
  if (homeSide) {
    for (const f of candidates.filter((o) => o.dir === homeSide)) {
      cloudMin = Math.min(cloudMin, f.value);
      cloudMax = Math.max(cloudMax, f.value);
      size++;
    }
  }

  return {
    touchedCount: T,
    cloud: { size, level, band: [cloudMin, cloudMax] },
    outlier: outliers.length === 1 ? outliers[0] : null, // single-outlier states
    outliers, // 0 / 1 / 2+ (same side) — render branches on side + count
  };
}
